// Copyright The OpenTelemetry Authors
// SPDX-License-Identifier: Apache-2.0

import { FormEvent, useEffect, useState } from 'react';
import * as S from './Footer.styled';
import PaymentBadges from './PaymentBadges';
import SessionGateway from '../../gateways/Session.gateway';
import { CypressFields } from '../../utils/Cypress';
import PlatformFlag from '../PlatformFlag';

const currentYear = new Date().getFullYear();

const categories = ['telescopes', 'binoculars', 'solar', 'accessories', 'books'];

const supportLinks = [
  { label: 'Shipping policy', href: '/policies/shipping' },
  { label: 'Returns & refunds', href: '/policies/returns' },
  { label: 'Privacy policy', href: '/policies/privacy' },
  { label: 'Terms of service', href: '/policies/terms' },
  { label: 'FAQ', href: '/#faq' },
];

const Footer = () => {
  const { userId } = SessionGateway.getSession();
  const [sessionId, setSessionId] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  useEffect(() => {
    setSessionId(userId);
  }, [userId]);

  const onSubscribe = (event: FormEvent) => {
    event.preventDefault();
    setSubscribed(true);
  };

  return (
    <S.Footer>
      <S.Inner>
        <div>
          <S.Brand>
            <S.BrandLogo />
            <S.BrandName>OpenTelemetry Demo</S.BrandName>
          </S.Brand>
          <S.Blurb>
            Astronomy gear for every kind of stargazer. A demonstration storefront run by Grafana Field Engineering on
            the OpenTelemetry demo. Not an actual shop.
          </S.Blurb>
          <S.Socials>
            <S.SocialLink
              href="https://github.com/open-telemetry/opentelemetry-demo"
              aria-label="Source code on GitHub"
              title="Source code on GitHub"
            >
              <svg width="18" height="18" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82a7.42 7.42 0 0 1 2-.27c.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.01 8.01 0 0 0 16 8c0-4.42-3.58-8-8-8z" />
              </svg>
            </S.SocialLink>
            <S.SocialLink href="https://grafana.com" aria-label="Grafana" title="Grafana">
              <img src="/images/grafana_icon.svg" alt="" width="18" height="18" />
            </S.SocialLink>
            <S.SocialLink href="https://opentelemetry.io" aria-label="OpenTelemetry" title="OpenTelemetry">
              <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M12.6974 13.1173c-1.0224 1.0224-1.0224 2.68 0 3.7024 1.0224 1.0224 2.68 1.0224 3.7024 0 1.0224-1.0223 1.0224-2.68 0-3.7024-1.0223-1.0223-2.68-1.0223-3.7024 0zm2.7677 2.7701c-.5063.5063-1.3267.5063-1.833 0s-.5063-1.3266 0-1.833c.5063-.5062 1.3267-.5062 1.833 0 .5063.504.5063 1.3267 0 1.833zM16.356.2355l-1.6041 1.6042c-.314.314-.314.83 0 1.144L21.015 9.247c.314.314.83.314 1.144 0l1.6042-1.6041c.314-.314.314-.83 0-1.144L17.4976.2354c-.314-.314-.8276-.314-1.1416 0zM5.1173 20.734c.2848-.2848.2848-.7497 0-1.0345l-.8155-.8155c-.2848-.2848-.7497-.2848-1.0345 0l-1.6845 1.6845-.0024.0024-.4625-.4625c-.2556-.2556-.6718-.2556-.925 0-.2556.2556-.2556.6718 0 .925l2.775 2.775c.2556.2556.6718.2556.925 0 .2532-.2556.2556-.6718 0-.925l-.4625-.4625.0024-.0024zm8.4856-15.893-3.5637 3.5637c-.3164.3164-.3164.8374 0 1.1538l2.2006 2.2005c1.5554-1.1197 3.7365-.981 5.1361.4187l1.7819-1.7818c.3164-.3165.3164-.8374 0-1.1538l-4.401-4.401c-.3165-.319-.8374-.319-1.1539 0zm-2.2881 7.8455-1.2999-1.2999c-.3043-.3043-.8033-.3043-1.1076 0l-4.5836 4.586c-.3042.3043-.3042.8033 0 1.1076l2.5973 2.5973c.3043.3043.8033.3043 1.1076 0l2.9478-2.9527c-.6231-1.2877-.5112-2.8431.3384-4.0383z" />
              </svg>
            </S.SocialLink>
            <S.SocialLink href="https://www.youtube.com/@Grafana" aria-label="Grafana on YouTube" title="Grafana on YouTube">
              <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
              </svg>
            </S.SocialLink>
            <S.SocialLink href="https://x.com/grafana" aria-label="Grafana on X" title="Grafana on X">
              <svg width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M14.234 10.162 22.977 0h-2.072l-7.591 8.824L7.251 0H.258l9.168 13.343L.258 24H2.33l8.016-9.318L16.749 24h6.993zm-2.837 3.299-.929-1.329L3.076 1.56h3.182l5.965 8.532.929 1.329 7.754 11.09h-3.182z" />
              </svg>
            </S.SocialLink>
            <S.SocialLink
              href="https://www.linkedin.com/company/grafana-labs"
              aria-label="Grafana on LinkedIn"
              title="Grafana on LinkedIn"
            >
              <svg width="17" height="17" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0z" />
              </svg>
            </S.SocialLink>
          </S.Socials>
        </div>
        <div>
          <S.ColumnTitle>Shop</S.ColumnTitle>
          {categories.map(category => (
            <S.ColumnLink key={category} href={`/?category=${category}#shop`}>
              {category}
            </S.ColumnLink>
          ))}
        </div>
        <div>
          <S.ColumnTitle>Customer support</S.ColumnTitle>
          {supportLinks.map(({ label, href }) => (
            <S.ColumnLink key={label} href={href}>
              {label}
            </S.ColumnLink>
          ))}
          <S.ColumnText>
            <span data-cy={CypressFields.SessionId}>session-id: {sessionId}</span>
          </S.ColumnText>
        </div>
        <div>
          <S.ColumnTitle>Join the stargazers</S.ColumnTitle>
          <S.NewsletterCopy>
            First access to new gear, dark-sky guides, and launch-night offers. Direct to your inbox.
          </S.NewsletterCopy>
          {subscribed ? (
            <S.NewsletterThanks>You&apos;re on the list. Clear skies!</S.NewsletterThanks>
          ) : (
            <S.NewsletterForm onSubmit={onSubscribe}>
              <S.NewsletterInput type="email" required placeholder="Enter your email" aria-label="Email address" />
              <S.NewsletterSubmit type="submit" aria-label="Subscribe">
                →
              </S.NewsletterSubmit>
            </S.NewsletterForm>
          )}
        </div>
      </S.Inner>
      <S.BottomBar>
        <S.BottomBarInner>
          <p>© {currentYear} Grafana Labs · Instrumented with OpenTelemetry · Observed with Grafana</p>
          <PaymentBadges />
        </S.BottomBarInner>
      </S.BottomBar>
      <PlatformFlag />
    </S.Footer>
  );
};

export default Footer;
